import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts'
import { formatCompactCurrencyNumber } from '../../../lib/utils'
import { localeForCurrency } from '../../../utils/currencyLocale'
import type { ArrearsBacklogBar } from '../arrearsBacklogDrill'

/** CR-8236: one bar, its due-date window included, handed back on click for its drill. */
type ArrearsBacklogData = ArrearsBacklogBar

interface ArrearsBacklogChartProps {
  data?: ArrearsBacklogData[]
  onBarClick?: (data: ArrearsBacklogData) => void
  showValue?: boolean // Whether to show value or count
  currency?: string
}

// Generate sample data if none provided
// Color based on relative severity
const getBarColor = (value: number, max: number): string => {
  const ratio = value / max
  if (ratio >= 0.8) return '#ef4444' // red
  if (ratio >= 0.6) return '#f97316' // orange
  if (ratio >= 0.4) return '#eab308' // yellow
  return '#10b981' // green
}

/** CR-8660: weeks run oldest first, 12+ down to 1, whatever order the payload holds them in.
 *  A label that is not a week sorts last rather than leaving the order to chance. */
const weeksOverdue = (bar: ArrearsBacklogData): number => parseInt(bar.week, 10) || 0

export function ArrearsBacklogChart({
  data,
  onBarClick,
  showValue = true,
  currency = 'GBP',
}: ArrearsBacklogChartProps) {
  const chartData = [...(data || [])].sort((a, b) => weeksOverdue(b) - weeksOverdue(a))
  const dataKey = showValue ? 'value' : 'count'
  // CR-7714: a bar with no value (null) colours as an empty bar.
  const maxValue = Math.max(...chartData.map(d => d[dataKey] ?? 0))

  const formatYAxis = (value: number) => {
    if (showValue) {
      // Same "3.4Cr" / "3.1M" shape as every other money axis on the
      // dashboard — CR-7120: numbers only, no currency symbol on an axis.
      return formatCompactCurrencyNumber(value, currency)
    }
    return value.toString()
  }

  const formatTooltipValue = (value: number) => {
    if (showValue) {
      return new Intl.NumberFormat(localeForCurrency(currency), {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value).replace('US$', '$')
    }
    return `${value} Arrears Lines`
  }

  // CR-3833: Click handler on Bar directly for reliable drill-down with Cell components
  const handleBarClick = (data: unknown) => {
    if (onBarClick && data) {
      const barData = data as ArrearsBacklogData
      onBarClick(barData)
    }
  }

  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart
        data={chartData}
        style={{ cursor: onBarClick ? 'pointer' : 'default' }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
        <XAxis
          dataKey="week"
          tick={{ fontSize: 11 }}
          label={{ value: 'Weeks Overdue', position: 'insideBottom', offset: -2, fontSize: 10, fill: '#9ca3af' }}
        />
        <YAxis
          tick={{ fontSize: 11 }}
          tickFormatter={formatYAxis}
        />
        <Tooltip
          formatter={(value: number) => [formatTooltipValue(value), showValue ? 'Outstanding Value' : 'Arrears Lines']}
          cursor={onBarClick ? { fill: 'rgba(59, 130, 246, 0.15)', stroke: '#3b82f6', strokeWidth: 1 } : { fill: 'rgba(59, 130, 246, 0.1)' }}
          content={({ active, payload, label }) => {
            if (active && payload && payload.length > 0) {
              return (
                <div className="bg-white border rounded-lg shadow-lg p-3 text-sm">
                  <p className="font-medium mb-1">Week {label}</p>
                  <p className="text-gray-600">{formatTooltipValue(payload[0].value as number)}</p>
                  {onBarClick && (
                    <p className="text-xs text-blue-600 mt-2 font-medium">Click to view details →</p>
                  )}
                </div>
              )
            }
            return null
          }}
        />
        <Bar
          dataKey={dataKey}
          name={showValue ? 'Outstanding Value' : 'Arrears Lines'}
          radius={[4, 4, 0, 0]}
          onClick={onBarClick ? handleBarClick : undefined}
          cursor={onBarClick ? 'pointer' : undefined}
        >
          {chartData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={getBarColor(entry[dataKey] ?? 0, maxValue)} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  )
}

export default ArrearsBacklogChart
